/* eslint-disable import/no-unresolved */
import { LocationPreview } from '@/features/events/editor/LocationPreview';
import { VenuePicker } from './VenuePicker';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import {
  EntryTypeCover,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  EntryTypeTicketed,
} from '@/lib/api/apiTypes';
import { DESIGN_TOKENS, THEME } from '@/lib/design/theme';
import { EVENT_AGE_RESTRICTIONS } from '@/features/events/eventTags';
import { createSentryInputTracker } from '@/lib/analytics/sentry';
import DateTimePicker, {
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';
import * as React from 'react';
import { CheckIcon } from 'lucide-react-native';
import { Keyboard, Platform, Pressable, View } from 'react-native';
import { ENTRY_TYPE_OPTIONS } from './constants';
import type { EventDraft, PickerField, ScheduleStepProps } from './types';
import {
  buildDateTime,
  formatDate,
  formatTime,
  getDurationHint,
  getEntryTypeGuidance,
  getFlyerFieldHint,
  getPickerValue,
} from './utils';

function sectionInsetStyle() {
  return {
    borderColor: DESIGN_TOKENS.colors.borderStrong,
    backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
  } as const;
}

const FIELD_ERROR_COLOR = THEME.dark.destructive;
type ScheduleValidationField = ScheduleStepProps['validationIssues'][number]['field'];

function FieldMetaLabel({ label, meta }: {
  label: string;
  meta?: 'Optional' | 'Required';
}) {
  return (
    <View className="flex-row items-center gap-2 px-1">
      <Text variant="small" className="font-semibold text-muted-foreground">
        {label}
      </Text>
      {meta ? (
        <Text
          variant="small"
          className="font-medium"
          style={{ color: DESIGN_TOKENS.colors.textSoft }}>
          {meta}
        </Text>
      ) : null}
    </View>
  );
}

function FieldIssue({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <Text variant="small" className="px-1 font-medium text-destructive">
      {message}
    </Text>
  );
}

function getFieldIssue(
  issues: ScheduleStepProps['validationIssues'],
  ...fields: ScheduleValidationField[]
) {
  return issues.find((issue) => fields.includes(issue.field))?.message ?? null;
}

function PickerTrigger({
  label,
  placeholder,
  invalid = false,
  onPress,
}: {
  label: string;
  placeholder?: string;
  invalid?: boolean;
  onPress: () => void;
}) {
  const isEmpty = !label.trim();
  return (
    <Pressable
      onPress={onPress}
      className="flex-1 rounded-[16px] border px-4 py-3.5"
      style={{
        borderColor: invalid ? FIELD_ERROR_COLOR : DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
      }}>
      <Text
        variant="default"
        style={{ color: isEmpty ? DESIGN_TOKENS.colors.textMuted : DESIGN_TOKENS.colors.text }}>
        {isEmpty ? (placeholder ?? '—') : label}
      </Text>
    </Pressable>
  );
}

function DateTimeFields({
  draft,
  dateField,
  timeField,
  activePicker,
  onDraftChange,
  onOpenPicker,
  onIOSPickerChange,
  issueMessage,
}: {
  draft: EventDraft;
  dateField: 'startsDate' | 'endsDate' | 'doorsDate';
  timeField: 'startsTime' | 'endsTime' | 'doorsTime';
  activePicker: PickerField | null;
  onDraftChange: (patch: Partial<EventDraft>) => void;
  onOpenPicker: (field: PickerField) => void;
  onIOSPickerChange: (field: PickerField, value: Date) => void;
  issueMessage?: string | null;
}) {
  const dateValue = getPickerValue(dateField, draft);
  const timeValue = getPickerValue(timeField, draft);
  const invalid = Boolean(issueMessage);

  if (Platform.OS === 'web') {
    const datetimeValue = `${draft[dateField]}T${draft[timeField]}`;
    return (
      <View className="gap-2">
        <input
          type="datetime-local"
          value={datetimeValue}
          onChange={(e) => {
            const [date, time] = e.target.value.split('T');
            if (date && time) {
              onDraftChange({ [dateField]: date, [timeField]: time } as Partial<EventDraft>);
            }
          }}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 22,
            border: `1px solid ${invalid ? FIELD_ERROR_COLOR : DESIGN_TOKENS.colors.borderStrong}`,
            backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
            color: DESIGN_TOKENS.colors.text,
            fontSize: 15,
            paddingLeft: 16,
            paddingRight: 16,
            colorScheme: 'light',
          } as React.CSSProperties}
        />
        <FieldIssue message={issueMessage ?? null} />
      </View>
    );
  }

  return (
    <View className="gap-3">
      <View className="flex-row gap-3">
        <PickerTrigger
          label={formatDate(draft[dateField])}
          placeholder="Date"
          invalid={invalid}
          onPress={() => onOpenPicker(dateField)}
        />
        <PickerTrigger
          label={formatTime(draft[timeField])}
          placeholder="Time"
          invalid={invalid}
          onPress={() => onOpenPicker(timeField)}
        />
      </View>
      <FieldIssue message={issueMessage ?? null} />

      {Platform.OS === 'ios' && activePicker === dateField ? (
        <View
          className="overflow-hidden rounded-[22px] border px-3 py-2"
          style={{
            borderColor: DESIGN_TOKENS.colors.borderStrong,
            backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
          }}>
          <DateTimePicker
            value={dateValue}
            mode="date"
            display="inline"
            onChange={(_event: DateTimePickerEvent, selectedDate?: Date) => {
              if (selectedDate) {
                onIOSPickerChange(dateField, selectedDate);
              }
            }}
          />
        </View>
      ) : null}

      {Platform.OS === 'ios' && activePicker === timeField ? (
        <View
          className="overflow-hidden rounded-[22px] border px-3 py-2"
          style={{
            borderColor: DESIGN_TOKENS.colors.borderStrong,
            backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
          }}>
          <DateTimePicker
            value={timeValue}
            mode="time"
            display="spinner"
            onChange={(_event: DateTimePickerEvent, selectedDate?: Date) => {
              if (selectedDate) {
                onIOSPickerChange(timeField, selectedDate);
              }
            }}
          />
        </View>
      ) : null}
    </View>
  );
}

export function ScheduleStep({
  draft,
  flyerReview,
  addressSuggestions,
  isLoadingAddressSuggestions,
  hasPinnedLocation,
  isResolvingAddress,
  addressLookupError,
  activePicker,
  validationIssues,
  onChange,
  onVenueSelect,
  onAddressChange,
  onSelectAddressSuggestion,
  onOpenPicker,
  onClosePicker,
  onPickerChange,
}: ScheduleStepProps) {
  const startsAt = buildDateTime(draft.startsDate, draft.startsTime);
  const endsAt = buildDateTime(draft.endsDate, draft.endsTime);
  const durationHint = getDurationHint(startsAt, endsAt);
  const [manualMode, setManualMode] = React.useState(
    () => draft.venueId === null && Boolean(draft.venueName.trim() || draft.address.trim()),
  );
  const [showDoorsTime, setShowDoorsTime] = React.useState(
    () => Boolean(draft.doorsDate && draft.doorsTime),
  );
  const [showEndTime, setShowEndTime] = React.useState(
    () => Boolean(draft.endsDate && draft.endsTime),
  );
  const priceIssue = getFieldIssue(validationIssues, 'coverPrice');
  const ticketUrlIssue = getFieldIssue(validationIssues, 'ticketUrl');
  const venueNameIssue = getFieldIssue(validationIssues, 'venueName');
  const addressIssue = getFieldIssue(validationIssues, 'address');
  const locationIssue = getFieldIssue(validationIssues, 'location');
  const doorsAtIssue = getFieldIssue(validationIssues, 'doorsAt');
  const startsAtIssue = getFieldIssue(validationIssues, 'startsAt');
  const endsAtIssue = getFieldIssue(validationIssues, 'endsAt');

  React.useEffect(() => {
    if (draft.doorsDate && draft.doorsTime) {
      setShowDoorsTime(true);
    }
  }, [draft.doorsDate, draft.doorsTime]);

  React.useEffect(() => {
    if (draft.endsDate && draft.endsTime) {
      setShowEndTime(true);
    }
  }, [draft.endsDate, draft.endsTime]);

  const venueNameInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'event_editor', field: 'venue_name', form: 'event_editor' }),
    []
  );
  const addressInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'event_editor', field: 'address', form: 'event_editor' }),
    []
  );

  function handleVenueNotListed(typedQuery: string) {
    setManualMode(true);
    if (typedQuery.trim()) {
      onChange({ venueName: typedQuery.trim(), venueId: null });
    }
  }

  function handleShowDoorsTime() {
    Keyboard.dismiss();
    onClosePicker();
    setShowDoorsTime(true);
  }

  function handleShowEndTime() {
    Keyboard.dismiss();
    onClosePicker();
    setShowEndTime(true);
  }

  function handleRemoveDoorsTime() {
    Keyboard.dismiss();
    onClosePicker();
    setShowDoorsTime(false);
    onChange({ doorsDate: '', doorsTime: '' });
  }

  function handleRemoveEndTime() {
    Keyboard.dismiss();
    onClosePicker();
    setShowEndTime(false);
    onChange({ endsDate: '', endsTime: '' });
  }

  return (
    <View className="gap-7">
      <View className="gap-2">
        <Text variant="small" className="px-1 font-semibold text-muted-foreground">Entry type · Required</Text>
        <View
          className="overflow-hidden rounded-[20px]"
          style={{ borderWidth: 1, borderColor: DESIGN_TOKENS.colors.borderStrong }}>
          {ENTRY_TYPE_OPTIONS.map((option, idx) => {
            const active = draft.entryType === option.value;
            const flyerHint = idx === 0 ? getFlyerFieldHint(flyerReview, 'pricing') : undefined;
            return (
              <Pressable
                key={option.value}
                onPress={() => onChange({ entryType: option.value })}
                className="flex-row items-center p-4"
                style={{
                  borderTopWidth: idx > 0 ? 1 : 0,
                  borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                  backgroundColor: active
                    ? `${DESIGN_TOKENS.colors.accentStrong}18`
                    : DESIGN_TOKENS.colors.surfaceAlt,
                }}>
                <View className="flex-1">
                  <Text
                    variant="default"
                    style={{
                      color: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.text,
                      fontWeight: active ? '600' : '400',
                    }}>
                    {option.label}
                  </Text>
                  {flyerHint ? (
                    <Text variant="small" style={{ color: DESIGN_TOKENS.colors.textSoft, marginTop: 1 }}>
                      {flyerHint}
                    </Text>
                  ) : null}
                </View>
                {active ? (
                  <View
                    className="size-5 items-center justify-center rounded-full"
                    style={{ backgroundColor: DESIGN_TOKENS.colors.accentStrong }}>
                    <Icon as={CheckIcon} size={11} style={{ color: '#000' }} />
                  </View>
                ) : (
                  <View
                    className="size-5 rounded-full"
                    style={{ borderWidth: 1.5, borderColor: DESIGN_TOKENS.colors.borderStrong }}
                  />
                )}
              </Pressable>
            );
          })}
        </View>
        <View className="rounded-[14px] border px-3 py-2.5" style={sectionInsetStyle()}>
          <Text variant="small" className="text-muted-foreground">
            {getEntryTypeGuidance(draft.entryType)}
          </Text>
        </View>

        {draft.entryType === EntryTypeCover || draft.entryType === EntryTypeTicketed ? (
          <View className="gap-2">
            <FieldMetaLabel
              label={draft.entryType === EntryTypeTicketed ? 'Ticket price from' : 'Cover charge'}
              meta="Required"
            />
            <Input
              value={draft.coverPrice}
              onChangeText={(value) => onChange({ coverPrice: value })}
              placeholder={
                draft.entryType === EntryTypeTicketed
                  ? 'Starting price (e.g. 25.00)'
                  : 'Cover charge (e.g. 10.00)'
              }
              keyboardType="decimal-pad"
              aria-invalid={Boolean(priceIssue)}
              className={priceIssue ? 'border-destructive' : undefined}
            />
            <FieldIssue message={priceIssue} />
          </View>
        ) : null}

        {draft.entryType === EntryTypeCover ? (
          <Input
            value={draft.coverNotes}
            onChangeText={(value) => onChange({ coverNotes: value })}
            placeholder="e.g. Ladies free before 11pm"
          />
        ) : null}

        {draft.entryType === EntryTypeTicketed ? (
          <View className="gap-2">
            <Input
              value={draft.ticketUrl}
              onChangeText={(value) => onChange({ ticketUrl: value })}
              placeholder="Ticket link · Required"
              keyboardType="url"
              autoCapitalize="none"
              autoCorrect={false}
              aria-invalid={Boolean(ticketUrlIssue)}
              className={ticketUrlIssue ? 'border-destructive' : undefined}
            />
            <FieldIssue message={ticketUrlIssue} />
            <Input
              value={draft.coverNotes}
              onChangeText={(value) => onChange({ coverNotes: value })}
              placeholder="Pricing tiers (e.g. $25 GA · $50 VIP)"
            />
          </View>
        ) : null}

        {draft.entryType === EntryTypeRSVP || draft.entryType === EntryTypeFreeRSVP ? (
          <Input
            value={draft.rsvpLimit}
            onChangeText={(value) => onChange({ rsvpLimit: value })}
            placeholder="Leave blank for unlimited"
            keyboardType="number-pad"
          />
        ) : null}
      </View>

      <View className="gap-2">
        <Text variant="small" className="px-1 font-semibold text-muted-foreground">Age restriction</Text>
        <View className="flex-row gap-2">
          {EVENT_AGE_RESTRICTIONS.map((option) => {
            const active = draft.ageRestriction === option.value;
            return (
              <Pressable
                key={option.value}
                className="flex-1 items-center justify-center rounded-[16px] p-4"
                style={{
                  backgroundColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.surfaceAlt,
                  borderWidth: 1,
                  borderColor: active ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.borderStrong,
                }}
                onPress={() => onChange({ ageRestriction: option.value })}>
                <Text
                  variant="default"
                  className="font-semibold"
                  style={{ color: active ? DESIGN_TOKENS.colors.textInverse : DESIGN_TOKENS.colors.text }}>
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      <View className="gap-2">
        <Text
          variant="small"
          className="px-1 font-semibold text-muted-foreground"
          style={getFlyerFieldHint(flyerReview, 'venue')
            ? { color: flyerReview?.needsReview.includes('venue') ? '#f59b80' : DESIGN_TOKENS.colors.accentStrong }
            : {}}>
          Venue · Required
        </Text>
        <VenuePicker
          value={
            draft.venueName && !manualMode
              ? { name: draft.venueName, id: draft.venueId ?? undefined, address: draft.address, lat: draft.latitude, lng: draft.longitude }
              : null
          }
          onChange={(venue) => {
            setManualMode(false);
            onVenueSelect(venue);
          }}
          onVenueNotListed={handleVenueNotListed}
          placeholder="Search venues..."
        />

        {manualMode ? (
          <View
            className="gap-3 rounded-[16px] border p-3"
            style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
            <Text variant="small" className="text-muted-foreground">
              Use this only if the venue cannot be found above.
            </Text>
            <View className="gap-2">
              <FieldMetaLabel label="Venue name" meta="Required" />
              <Input
                value={draft.venueName}
                onChangeText={(value) => {
                  venueNameInputTracker.changeText(value);
                  onChange({ venueName: value, venueId: null });
                }}
                onFocus={() => {
                  onClosePicker();
                  venueNameInputTracker.focus();
                }}
                onBlur={() => venueNameInputTracker.blur(draft.venueName)}
                placeholder="Name of the venue"
                aria-invalid={Boolean(venueNameIssue)}
                className={venueNameIssue ? 'border-destructive' : undefined}
              />
              <FieldIssue message={venueNameIssue} />
            </View>

            <View className="gap-2">
              <FieldMetaLabel label="Street address" meta="Required" />
              <Input
                value={draft.address}
                onChangeText={(value) => {
                  addressInputTracker.changeText(value);
                  onAddressChange(value);
                }}
                onFocus={() => {
                  onClosePicker();
                  addressInputTracker.focus();
                }}
                onBlur={() => addressInputTracker.blur(draft.address)}
                placeholder="Street address, city, state"
                aria-invalid={Boolean(addressIssue || locationIssue)}
                className={addressIssue || locationIssue ? 'border-destructive' : undefined}
              />
              <FieldIssue message={addressIssue ?? locationIssue} />
            </View>

            {isLoadingAddressSuggestions || addressSuggestions.length > 0 ? (
              <View
                className="rounded-[12px] border p-2"
                style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceRaised }}>
                {isLoadingAddressSuggestions ? (
                  <Text variant="small" className="p-3 text-muted-foreground">Looking up address…</Text>
                ) : (
                  addressSuggestions.map((suggestion) => (
                    <Pressable
                      key={`${suggestion.display_name}-${suggestion.lat}-${suggestion.lng}`}
                      className="rounded-[10px] p-3"
                      onPress={() => onSelectAddressSuggestion(suggestion)}>
                      <Text variant="small" className="text-foreground">{suggestion.display_name}</Text>
                    </Pressable>
                  ))
                )}
              </View>
            ) : null}

            <View className="gap-2">
              <Text variant="small" className="px-1 font-semibold text-muted-foreground">
                Pin preview
              </Text>
              <LocationPreview
                coordinate={{ latitude: draft.latitude, longitude: draft.longitude }}
                hasAddress={Boolean(draft.address.trim()) && hasPinnedLocation}
              />
            </View>

            {isResolvingAddress ? (
              <Text variant="small" className="text-muted-foreground">Pinning location…</Text>
            ) : null}
            {addressLookupError ? (
              <Text variant="small" className="text-destructive">{addressLookupError}</Text>
            ) : null}
          </View>
        ) : null}
        {!manualMode ? <FieldIssue message={venueNameIssue ?? addressIssue ?? locationIssue} /> : null}
      </View>

      <View className="gap-2">
        <Text variant="small" className="px-1 font-semibold text-muted-foreground">Schedule · Start required</Text>
        <View
          className="overflow-hidden rounded-[20px]"
          style={{ borderWidth: 1, borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
          {!showDoorsTime ? (
            <View className="px-4 py-3">
              <Pressable hitSlop={8} onPress={handleShowDoorsTime}>
                <Text variant="small" style={{ color: DESIGN_TOKENS.colors.accentStrong }}>+ Doors open</Text>
              </Pressable>
            </View>
          ) : null}

          {showDoorsTime ? (
            <>
              <View className="px-4 pb-4 pt-3.5">
                <View className="mb-3 flex-row items-center justify-between">
                  <Text variant="small" className="font-medium" style={{ color: DESIGN_TOKENS.colors.textSoft }}>Doors open</Text>
                  <Pressable hitSlop={8} onPress={handleRemoveDoorsTime}>
                    <Text variant="small" style={{ color: DESIGN_TOKENS.colors.textSoft }}>Remove</Text>
                  </Pressable>
                </View>
                <DateTimeFields
                  draft={draft}
                  dateField="doorsDate"
                  timeField="doorsTime"
                  activePicker={activePicker}
                  onDraftChange={onChange}
                  onOpenPicker={onOpenPicker}
                  onIOSPickerChange={onPickerChange}
                  issueMessage={doorsAtIssue}
                />
              </View>
            </>
          ) : null}

          <View style={{ height: 1, backgroundColor: DESIGN_TOKENS.colors.borderStrong, marginHorizontal: 16 }} />
          <View className="px-4 pb-4 pt-3.5">
            <Text variant="small" className="mb-3 font-medium" style={{ color: DESIGN_TOKENS.colors.textSoft }}>Starts</Text>
            <DateTimeFields
              draft={draft}
              dateField="startsDate"
              timeField="startsTime"
              activePicker={activePicker}
              onDraftChange={onChange}
              onOpenPicker={onOpenPicker}
              onIOSPickerChange={onPickerChange}
              issueMessage={startsAtIssue}
            />
          </View>

          {!showEndTime ? (
            <View
              className="px-4 py-3"
              style={{ borderTopWidth: 1, borderTopColor: DESIGN_TOKENS.colors.borderStrong }}>
              <Pressable hitSlop={8} onPress={handleShowEndTime}>
                <Text variant="small" style={{ color: DESIGN_TOKENS.colors.accentStrong }}>+ End time</Text>
              </Pressable>
            </View>
          ) : null}

          {showEndTime ? (
            <>
              <View style={{ height: 1, backgroundColor: DESIGN_TOKENS.colors.borderStrong, marginHorizontal: 16 }} />
              <View className="px-4 pb-4 pt-3.5">
                <View className="mb-3 flex-row items-center justify-between">
                  <Text variant="small" className="font-medium" style={{ color: DESIGN_TOKENS.colors.textSoft }}>Ends</Text>
                  <Pressable hitSlop={8} onPress={handleRemoveEndTime}>
                    <Text variant="small" style={{ color: DESIGN_TOKENS.colors.textSoft }}>Remove</Text>
                  </Pressable>
                </View>
                <DateTimeFields
                  draft={draft}
                  dateField="endsDate"
                  timeField="endsTime"
                  activePicker={activePicker}
                  onDraftChange={onChange}
                  onOpenPicker={onOpenPicker}
                  onIOSPickerChange={onPickerChange}
                  issueMessage={endsAtIssue}
                />
                {durationHint ? (
                  <Text
                    variant="small"
                    className="mt-2"
                    style={{ color: durationHint.tone === 'error' ? '#ef4e2e' : DESIGN_TOKENS.colors.textSoft }}>
                    {durationHint.label}
                  </Text>
                ) : null}
              </View>
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
}
